import { crmPost } from "./crm";
import { stableStringify } from "@cleo/shared/relaySigning";
export const CRM_CONTACTS_CHANGED_EVENT = "neucleos-crm-contacts-changed";
export interface ContactPreviewRow {
  externalId: string;
  name: string;
  organization: string;
  title: string;
  emails: string[];
  phones: string[];
  fingerprint: string;
  importedContactId?: string | null;
  matches?: { contactId: string; name: string; revision: number }[];
  moreMatches?: boolean;
}
export interface ContactPreviewPage {
  contacts: ContactPreviewRow[];
  cursor: string | null;
  sourceKey: string;
  generation?: number;
  pickerToken?: string;
}
export interface ContactImportChoice {
  externalId: string;
  fingerprint: string;
  resolution: "create" | "merge";
  contactId?: string;
  expectedRevision?: number;
}
export interface ContactImportRequest {
  sourceKey: string;
  generation?: number;
  pickerToken?: string;
  requestKey: string;
  selected: ContactImportChoice[];
}
export interface ContactImportResult {
  receiptId: string;
  items: { externalId: string; contactId: string; sourceId: string; outcome: string }[];
}
export async function contactMatches(
  worldKey: string,
  provider: string,
  sourceKey: string,
  contacts: ContactPreviewRow[],
): Promise<ContactPreviewRow[]> {
  for (let page = 0; page < 100; page++) {
    const prepared = await crmPost<{ ready: boolean }>(worldKey, {
      action: "prepareContactImports",
    });
    if (prepared.ready) {
      const result = await crmPost<{
        matches: {
          externalId: string;
          importedContactId: string | null;
          matches: NonNullable<ContactPreviewRow["matches"]>;
          moreMatches: boolean;
        }[];
      }>(worldKey, {
        action: "contactImportMatches",
        provider,
        sourceKey,
        contacts: contacts.map(
          ({ externalId, name, organization, title, emails, phones, fingerprint }) => ({
            externalId,
            name,
            organization,
            title,
            emails,
            phones,
            fingerprint,
          }),
        ),
      });
      const byId = new Map(result.matches.map((row) => [row.externalId, row]));
      return contacts.map((row) => ({ ...row, ...byId.get(row.externalId) }));
    }
  }
  throw new Error(
    "Existing contacts are still being prepared for duplicate checks. Retry the preview to continue.",
  );
}
export async function verifyContactImport(
  worldKey: string,
  request: ContactImportRequest,
  result: ContactImportResult,
): Promise<void> {
  const read = await crmPost<{
    receipt: (ContactImportResult & { request: ContactImportRequest }) | null;
  }>(worldKey, { action: "contactImportReceipt", requestKey: request.requestKey });
  if (
    read.receipt?.receiptId !== result.receiptId ||
    stableStringify(read.receipt.items) !== stableStringify(result.items) ||
    stableStringify(read.receipt.request.selected) !== stableStringify(request.selected)
  )
    throw new Error(
      "The import receipt did not match. Retry with the same selection to recover its result.",
    );
  for (let offset = 0; offset < result.items.length; offset += 5) {
    await Promise.all(
      result.items.slice(offset, offset + 5).map(async (item) => {
        const [{ contact }, { source }] = await Promise.all([
          crmPost<{ contact: { _id: string; emails?: string[]; phones?: string[] } }>(worldKey, {
            action: "contact",
            contactId: item.contactId,
          }),
          crmPost<{
            source: {
              contactId: string;
              externalId: string;
              snapshot: { emails: string[]; phones: string[] };
            };
          }>(worldKey, { action: "contactImportSource", sourceId: item.sourceId }),
        ]);
        if (
          contact._id !== item.contactId ||
          source.contactId !== item.contactId ||
          source.externalId !== item.externalId ||
          (item.outcome !== "already_imported" &&
            (!source.snapshot.emails.every((value) => contact.emails?.includes(value)) ||
              !source.snapshot.phones.every((value) => contact.phones?.includes(value))))
        )
          throw new Error(
            "An imported contact could not be independently confirmed. Retry the same selection.",
          );
      }),
    );
  }
  window.dispatchEvent(new CustomEvent(CRM_CONTACTS_CHANGED_EVENT, { detail: { worldKey } }));
}
